import React, { useState } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { ChevronRight, ChevronLeft } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// 12 questions, 3 dimensions × 4 each
const DIMENSIONS = [
  {
    key: 'perspectiva',
    label: 'Perspectiva',
    color: 'blue',
    emoji: '🔭',
    description: 'Capacidade de entender o ponto de vista do outro',
    questions: [
      'Quando ouço um colega falar sobre um problema, me esforço para entender a situação do ponto de vista dele.',
      'Antes de dar minha opinião, costumo me perguntar: "Como essa pessoa está enxergando essa situação?"',
      'Consigo identificar como diferentes pessoas do time interpretam o mesmo evento de formas distintas.',
      'Mesmo quando discordo, consigo compreender a lógica por trás da posição do outro.',
    ],
  },
  {
    key: 'cuidado',
    label: 'Cuidado Compassivo',
    color: 'pink',
    emoji: '💗',
    description: 'Agir com empatia diante do sofrimento alheio',
    questions: [
      'Quando alguém na equipe está passando por um momento difícil, sinto vontade genuína de ajudar.',
      'Me preocupo com o bem-estar emocional dos meus colegas, não só com os resultados do trabalho.',
      'Quando percebo que alguém está sobrecarregado, tomo alguma atitude — ofereço ajuda ou falo com a pessoa.',
      'Noto quando alguém está bem ou mal mesmo sem que essa pessoa diga explicitamente.',
    ],
  },
  {
    key: 'presenca',
    label: 'Presença Empática',
    color: 'green',
    emoji: '🌿',
    description: 'Estar disponível emocionalmente para as pessoas',
    questions: [
      'Quando alguém fala comigo, consigo dar atenção plena — sem checar o celular ou pensar em outra coisa.',
      'Pessoas à minha volta se sentem à vontade para me contar sobre dificuldades pessoais ou profissionais.',
      'Consigo estar presente em uma conversa difícil sem me desconectar emocionalmente ou mudar de assunto.',
      'Quando alguém precisa de mim, consigo colocar minhas próprias preocupações de lado para ouvir de verdade.',
    ],
  },
];

const LEVEL_INFO = {
  baseline: {
    title: 'Avaliação Inicial de Empatia',
    subtitle: 'Esta é sua avaliação de entrada. Ela serve como ponto de partida para acompanharmos sua evolução ao longo do treinamento.',
    emoji: '🌱',
  },
  midpoint: {
    title: 'Avaliação de Empatia — Meio do Caminho',
    subtitle: 'Você está no meio da jornada! Esta avaliação mostra como suas habilidades empáticas evoluíram desde o início.',
    emoji: '🌿',
  },
  final: {
    title: 'Avaliação Final de Empatia',
    subtitle: 'Parabéns por chegar até aqui! Esta é sua avaliação final — ela fecha o ciclo e revela o quanto você cresceu.',
    emoji: '🌳',
  },
};

const COLOR_CLASSES = {
  blue: {
    bg: 'bg-blue-50',
    border: 'border-blue-200',
    active: 'bg-blue-600 border-blue-600 text-white',
    label: 'text-blue-700',
    bar: 'bg-blue-500',
  },
  pink: {
    bg: 'bg-pink-50',
    border: 'border-pink-200',
    active: 'bg-pink-600 border-pink-600 text-white',
    label: 'text-pink-700',
    bar: 'bg-pink-500',
  },
  green: {
    bg: 'bg-green-50',
    border: 'border-green-200',
    active: 'bg-green-600 border-green-600 text-white',
    label: 'text-green-700',
    bar: 'bg-green-500',
  },
};

const SCALE_LABELS = ['Nunca', 'Raramente', 'Às vezes', 'Frequentemente', 'Sempre'];

/**
 * EmpatiaScale — JSE-inspired empathy assessment shown at 3 levels.
 *
 * Props:
 *   level   — "baseline" | "midpoint" | "final"
 *   onClose — called when dismissed or submitted
 */
const EmpatiaScale = ({ level = 'baseline', onClose }) => {
  // Flatten all 12 questions into a single queue
  const allQuestions = DIMENSIONS.flatMap((dim) =>
    dim.questions.map((text, i) => ({ dimKey: dim.key, dimLabel: dim.label, color: dim.color, emoji: dim.emoji, text, idx: i }))
  );

  const [currentQ, setCurrentQ] = useState(0);
  const [answers, setAnswers] = useState({}); // "perspectiva_0": 3, ...
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [showIntro, setShowIntro] = useState(true);

  const info = LEVEL_INFO[level];
  const q = allQuestions[currentQ];
  const answerKey = `${q?.dimKey}_${q?.idx}`;
  const totalQ = allQuestions.length;
  const progress = Math.round((Object.keys(answers).length / totalQ) * 100);

  const handleAnswer = (val) => {
    setAnswers((prev) => ({ ...prev, [answerKey]: val }));
  };

  const canGoNext = answers[answerKey] !== undefined;
  const isLast = currentQ === totalQ - 1;

  const goNext = () => {
    if (isLast) {
      submitAnswers();
    } else {
      setCurrentQ((i) => i + 1);
    }
  };

  const goPrev = () => {
    if (currentQ > 0) setCurrentQ((i) => i - 1);
  };

  const submitAnswers = async () => {
    setIsSubmitting(true);
    try {
      // Build dimension arrays
      const buildDim = (dimKey) =>
        [0, 1, 2, 3].map((i) => answers[`${dimKey}_${i}`] || 3);

      await axios.post(
        `${API}/surveys/empathy`,
        {
          level,
          perspectiva: buildDim('perspectiva'),
          cuidado: buildDim('cuidado'),
          presenca: buildDim('presenca'),
        },
        { withCredentials: true }
      );
      setSubmitted(true);
      setTimeout(onClose, 2500);
    } catch {
      onClose();
    } finally {
      setIsSubmitting(false);
    }
  };

  const colors = COLOR_CLASSES[q?.color || 'blue'];

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg my-6 p-6 relative">

        {submitted ? (
          <div className="text-center py-10">
            <div className="text-5xl mb-4">✨</div>
            <p className="text-xl font-bold text-gray-900">Avaliação concluída!</p>
            <p className="text-sm text-gray-500 mt-2 leading-relaxed">
              Seus resultados foram registrados. Continue o treinamento para acompanhar sua evolução.
            </p>
          </div>
        ) : showIntro ? (
          /* Intro screen */
          <div className="text-center py-4">
            <div className="text-5xl mb-4">{info.emoji}</div>
            <h3 className="text-xl font-bold text-gray-900 mb-2">{info.title}</h3>
            <p className="text-sm text-gray-500 leading-relaxed mb-6">{info.subtitle}</p>

            <div className="grid grid-cols-3 gap-3 mb-8">
              {DIMENSIONS.map((dim) => {
                const c = COLOR_CLASSES[dim.color];
                return (
                  <div key={dim.key} className={`rounded-xl p-3 ${c.bg} border ${c.border}`}>
                    <div className="text-xl mb-1">{dim.emoji}</div>
                    <p className={`text-xs font-semibold ${c.label}`}>{dim.label}</p>
                    <p className="text-xs text-gray-500 mt-1 leading-tight">{dim.description}</p>
                  </div>
                );
              })}
            </div>

            <p className="text-xs text-gray-400 mb-6">12 perguntas · cerca de 3 minutos</p>

            <div className="flex gap-3">
              <Button variant="outline" className="flex-1" onClick={onClose}>
                Agora não
              </Button>
              <Button
                className="flex-1 bg-purple-600 hover:bg-purple-700"
                onClick={() => setShowIntro(false)}
              >
                Começar
              </Button>
            </div>
          </div>
        ) : (
          /* Question screen */
          <>
            {/* Header */}
            <div className="flex items-center justify-between mb-4">
              <span className={`text-xs font-semibold px-2 py-1 rounded-full ${colors.bg} ${colors.label}`}>
                {q.emoji} {q.dimLabel}
              </span>
              <span className="text-xs text-gray-400">{currentQ + 1} / {totalQ}</span>
            </div>

            {/* Progress */}
            <div className="w-full bg-gray-100 rounded-full h-1.5 mb-6">
              <div
                className={`h-1.5 rounded-full transition-all duration-300 ${colors.bar}`}
                style={{ width: `${((currentQ + 1) / totalQ) * 100}%` }}
              />
            </div>

            {/* Question */}
            <p className="text-base font-medium text-gray-800 mb-6 leading-relaxed min-h-[64px]">
              {q.text}
            </p>

            {/* Scale */}
            <div className="flex gap-2 justify-between mb-2">
              {[1, 2, 3, 4, 5].map((val) => (
                <button
                  key={val}
                  onClick={() => handleAnswer(val)}
                  className={`flex-1 flex flex-col items-center gap-1 py-3 rounded-xl border-2 transition-all ${
                    answers[answerKey] === val
                      ? colors.active + ' scale-105 shadow-md'
                      : `${colors.border} ${colors.bg} hover:border-opacity-60 text-gray-600`
                  }`}
                >
                  <span className="text-lg font-bold">{val}</span>
                </button>
              ))}
            </div>
            <div className="flex justify-between text-xs text-gray-400 px-1 mb-8">
              <span>{SCALE_LABELS[0]}</span>
              <span>{SCALE_LABELS[4]}</span>
            </div>

            {/* Navigation */}
            <div className="flex gap-3">
              <Button
                variant="outline"
                className="w-10 h-10 p-0"
                onClick={goPrev}
                disabled={currentQ === 0}
              >
                <ChevronLeft className="w-5 h-5" />
              </Button>
              <Button
                className={`flex-1 ${isLast ? 'bg-purple-600 hover:bg-purple-700' : 'bg-gray-800 hover:bg-gray-700'}`}
                disabled={!canGoNext || isSubmitting}
                onClick={goNext}
              >
                {isLast
                  ? (isSubmitting ? 'Enviando…' : 'Concluir avaliação')
                  : (
                    <span className="flex items-center gap-1">
                      Próxima <ChevronRight className="w-4 h-4" />
                    </span>
                  )}
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default EmpatiaScale;
